<template>
  <div class="d-flex flex-column pt-3">
    <form @submit.prevent="this.$route.params.id ? updatePartner() : createPartner()" class="cutax-form">
      <div class="d-flex justify-content-center">
        <div class="card cutax-form-card">
          <div class="card-body">
            <h4 class="text-center">{{$route.params.id ? "Edit": "Create"}} {{ tax.name ?? 'Name' }} fee</h4>
            <div class="d-flex flex-column gap-3">
              <div>
                <label for="name" class="form-label">Name</label>
                <input type="text" class="form-control" id="name" v-model="tax.name">
                <p v-if="validationTaxErrors?.name" v-for="names in validationTaxErrors.name" class="text-danger">{{ names }}</p>
              </div>

              <div class="d-flex gap-3 flex-wrap">
                <div class="cutax-field cutax-field-type">
                  <label for="type" class="form-label">Type</label>
                  <select id="type" class="form-control" v-model.number="tax.type">
                    <option value="1">Country</option>
                    <option value="2">Personal</option>
                    <option value="3">Default (platform)</option>
                  </select>
                </div>

                <div class="cutax-field cutax-field-country" v-if="Number(tax.type) === 1">
                  <label for="country" class="form-label">Country</label>
                  <select id="country" class="form-control" v-model="selectedCountry" @change="onCountryChange">
                    <option value="">All</option>
                    <option
                        v-for="country in countries"
                        :key="country.value"
                        :value="country.value"
                    >
                      {{ country.label }}
                    </option>
                  </select>
                  <p v-if="validationTaxErrors?.country_code" v-for="error in validationTaxErrors.country_code" class="text-danger small mb-0">{{ error }}</p>
                </div>

                <div class="cutax-field cutax-field-currency" v-if="Number(tax.type) === 1">
                  <label for="currency" class="form-label">Currency</label>
                  <select id="currency" class="form-control" v-model="tax.currency">
                    <option value="">Any</option>
                    <option v-for="currency in availableCurrencies" :key="currency" :value="currency">{{ currency }}</option>
                  </select>
                  <p v-if="validationTaxErrors?.currency" v-for="error in validationTaxErrors.currency" class="text-danger small mb-0">{{ error }}</p>
                </div>
              </div>
              <!-- Optional validity range -->
              <div class="d-flex gap-3 flex-wrap">
                <div class="cutax-field cutax-field-date">
                  <label for="valid_from" class="form-label">Valid from <small class="text-muted">(Vilnius timezone)</small></label>
                  <input type="date" class="form-control" id="valid_from" v-model="tax.valid_from">
                  <p v-if="validationTaxErrors?.valid_from" v-for="error in validationTaxErrors.valid_from" class="text-danger small mb-0">{{ error }}</p>
                </div>
                <div class="cutax-field cutax-field-date">
                  <label for="valid_until" class="form-label">Valid until <small class="text-muted">(Vilnius timezone)</small></label>
                  <input type="date" class="form-control" id="valid_until" v-model="tax.valid_until">
                  <p v-if="validationTaxErrors?.valid_until" v-for="error in validationTaxErrors.valid_until" class="text-danger small mb-0">{{ error }}</p>
                </div>
                <small class="text-muted" v-if="Number(tax.type) === 3">
                  Applied to every channel without a personal or country rule while today is inside this range. Leave a date empty for an open-ended range.
                </small>
              </div>
              <!-- 2-3 Euro -->
              <div class="border rounded p-3 mb-2 bg-light text-dark">
                <h6 class="mb-3">Micro<span v-if="microThreshold"> (Up to {{ microThreshold }})</span></h6>
                <div class="d-flex gap-3 flex-wrap cutax-fee-row">
                  <div class="cutax-fee-field">
                    <label for="micro_percent" class="form-label">Percent</label>
                    <input type="text" inputmode="decimal" class="form-control" id="micro_percent" :value="formatDecimal(tax.micro_percent)" @input="handleDecimalInput('micro_percent', 0, 100, $event)">
                    <p v-if="validationTaxErrors?.micro_percent" v-for="error in validationTaxErrors.micro_percent" class="text-danger small mb-0">{{ error }}</p>
                  </div>
                  <div class="cutax-fee-field">
                    <label for="micro_fixed" class="form-label">Fixed Fee</label>
                    <input type="text" inputmode="decimal" class="form-control" id="micro_fixed" :value="formatDecimal(tax.micro_fixed)" @input="handleDecimalInput('micro_fixed', 0, 10000, $event)">
                    <p v-if="validationTaxErrors?.micro_fixed" v-for="error in validationTaxErrors.micro_fixed" class="text-danger small mb-0">{{ error }}</p>
                  </div>
                </div>
              </div>

              <!-- Above 3 Euro -->
              <div class="border rounded p-3 mb-2 bg-light text-dark">
                <h6 class="mb-3">Regular<span v-if="microThreshold"> (Above {{ microThreshold }})</span></h6>
                <div class="d-flex gap-3 flex-wrap cutax-fee-row">
                  <div class="cutax-fee-field">
                    <label for="percent" class="form-label">Percent</label>
                    <input type="text" inputmode="decimal" class="form-control" id="percent" :value="formatDecimal(tax.percent)" @input="handleDecimalInput('percent', 0, 100, $event)">
                    <p v-if="validationTaxErrors?.percent" v-for="error in validationTaxErrors.percent" class="text-danger small mb-0">{{ error }}</p>
                  </div>
                  <div class="cutax-fee-field">
                    <label for="fixed" class="form-label">Fixed Fee</label>
                    <input type="text" inputmode="decimal" class="form-control" id="fixed" :value="formatDecimal(tax.fixed)" @input="handleDecimalInput('fixed', 0, 10000, $event)">
                    <p v-if="validationTaxErrors?.fixed" v-for="error in validationTaxErrors.fixed" class="text-danger small mb-0">{{ error }}</p>
                  </div>
                </div>
              </div>
              <div class="cutax-field cutax-field-vat">
                <label for="vat_percent" class="form-label">VAT Percent</label>
                <input type="text" inputmode="decimal" class="form-control" id="vat_percent" :value="formatDecimal(tax.vat_percent)" @input="handleDecimalInput('vat_percent', 0, 100, $event)">
                <p v-if="validationTaxErrors?.vat_percent" v-for="error in validationTaxErrors.vat_percent" class="text-danger">{{ error }}</p>
              </div>

              <div class="border rounded p-3 mb-2 bg-light text-dark">
                <h6 class="mb-3">Minimum Amounts</h6>
                <div class="d-flex gap-3 flex-wrap cutax-fee-row">
                  <div class="cutax-fee-field cutax-fee-field-wide">
                    <label for="minimal_payment_amount" class="form-label">Min Payment Amount</label>
                    <input type="text" inputmode="decimal" class="form-control" id="minimal_payment_amount" :value="formatDecimal(tax.minimal_payment_amount)" @input="handleDecimalInput('minimal_payment_amount', 0, 10000, $event)">
                    <p v-if="validationTaxErrors?.minimal_payment_amount" v-for="error in validationTaxErrors.minimal_payment_amount" class="text-danger small mb-0">{{ error }}</p>
                    <small class="text-muted d-block mt-1">
                      Smallest total the supporter pays (price + platform fee + VAT). If the calculated total is lower, the fee is raised to reach this amount.
                      When the price is at the creator minimum (below), the total is set to exactly this amount, even if the fee has to be lowered.
                    </small>
                  </div>
                  <div class="cutax-fee-field cutax-fee-field-wide">
                    <label for="minimal_creator_amount" class="form-label">Min Creator Amount</label>
                    <input type="text" inputmode="decimal" class="form-control" id="minimal_creator_amount" :value="formatDecimal(tax.minimal_creator_amount)" @input="handleDecimalInput('minimal_creator_amount', 0, 10000, $event)">
                    <p v-if="validationTaxErrors?.minimal_creator_amount" v-for="error in validationTaxErrors.minimal_creator_amount" class="text-danger small mb-0">{{ error }}</p>
                    <small class="text-muted d-block mt-1">
                      Smallest price a creator can charge, before fees and VAT. Any price below it is raised to this amount.
                      Set 0 for no minimum. Example: 1.00 means a 0.50 product is sold for 1.00.
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="d-flex justify-content-center mb-3">
        <div class="cutax-form-card d-flex justify-content-end">
          <button
            type="submit"
            class="btn btn-primary"
            :disabled="loading"
          >
            {{ this.$route.params.id ? 'Update' : 'Create' }}
          </button>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import axios from 'axios';
import router from "@/router";


export default {
  data() {
    return {
      loading: false,
      tax: {
        type: 1,
        name: '',
        currency: '',
        micro_percent: '',
        micro_fixed: '',
        percent: '',
        fixed: '',
        one_percent: '',
        one_fixed: '',
        vat_percent: '',
        minimal_payment_amount: '',
        minimal_creator_amount: '',
        valid_from: '',
        valid_until: '',
      },
      countries: [],
      selectedCountry: '',
      validationTaxErrors: []
    };
  },

  methods: {
     fetchTax(id) {
      this.loading = true;
      axios.get(`admin/fee-rules/${id}`)
          .then(response => {
            this.tax = {
              ...response.data,
              currency: response.data.currency || '',
              valid_from: (response.data.valid_from || '').slice(0, 10),
              valid_until: (response.data.valid_until || '').slice(0, 10),
            };
            if (response.data.country_code && typeof response.data.country_code === 'object') {
              this.selectedCountry = response.data.country_code.value || '';
            } else {
              this.selectedCountry = response.data.country_code || '';
            }
            this.loading = false;
          })
          .catch(error => {
            console.error('Error fetching tax:', error);
            this.loading = false;
          });
    },

    getCountries() {
      this.loading = true;
      axios.get(`/admin/countries`)
          .then(response => {
            this.countries = Array.isArray(response.data) ? response.data : [];
            this.loading = false;
          })
          .catch(error => {
            console.error('Error fetching countries:', error);
            this.loading = false;
          });
    },

    async createPartner() {
      this.loading = true;
      this.validationTaxErrors = [];

      const payload = {
        name: this.tax.name || '',
        type: this.tax.type || '',
        currency: Number(this.tax.type) === 1 ? (this.tax.currency || null) : null,
        micro_percent: this.tax.micro_percent || '',
        micro_fixed: this.tax.micro_fixed || '',
        percent: this.tax.percent || '',
        fixed: this.tax.fixed || '',
        one_percent: this.tax.micro_percent || '',
        one_fixed: this.tax.micro_fixed || '',
        vat_percent: this.tax.vat_percent || '',
        minimal_payment_amount: this.tax.minimal_payment_amount || '',
        minimal_creator_amount: this.tax.minimal_creator_amount || '',
        valid_from: this.tax.valid_from || null,
        valid_until: this.tax.valid_until || null,
      };

      if (Number(this.tax.type) === 1 && this.selectedCountry) {
        payload.country_code = this.selectedCountry;
      }

      try {
        await axios.post('admin/fee-rules', payload, {
          headers: {
            'Content-Type': 'application/json',
          }
        });

        await router.push(`/fees/`);

        alert('The fee rule has been successfully created');

      } catch (error) {
        if (error.response?.status === 422) {
          this.validationTaxErrors = error.response.data.errors;
        } else {
          console.error('Error creating tax rule:', error);
        }
      } finally {
        this.loading = false;
      }
    },


    async updatePartner() {
      this.loading = true;
      this.validationTaxErrors = [];
      const tax = this.tax;
      if (!tax) return;

      const payload = {
        name: tax.name || '',
        type: tax.type || '',
        currency: Number(tax.type) === 1 ? (tax.currency || null) : null,
        micro_percent: tax.micro_percent || '',
        micro_fixed: tax.micro_fixed || '',
        percent: tax.percent || '',
        fixed: tax.fixed || '',
        one_percent: tax.micro_percent || '',
        one_fixed: tax.micro_fixed || '',
        vat_percent: tax.vat_percent || '',
        minimal_payment_amount: tax.minimal_payment_amount || '',
        minimal_creator_amount: tax.minimal_creator_amount || '',
        valid_from: tax.valid_from || null,
        valid_until: tax.valid_until || null,
      };

      if (Number(tax.type) === 1 && this.selectedCountry) {
        payload.country_code = this.selectedCountry;
      }

      try {
        await axios.patch(
            `admin/fee-rules/${this.$route.params.id}`,
            payload,
            {
              headers: {
                'Content-Type': 'application/json',
              },
            }
        );

        alert('The fee rule is successfully updated');

      } catch (error) {
        if (error.response?.status === 422) {
          this.validationTaxErrors = error.response.data.errors;
        } else {
          console.error('Error updating tax:', error);
        }
      } finally {
        this.loading = false;
      }
    },


    // Runs on user selection only, so loading a saved rule never overwrites its currency.
    onCountryChange() {
      const country = this.countries.find(c => c.value === this.selectedCountry);
      const currencies = country?.registrationCurrencies || [];
      if (currencies.length === 1) {
        this.tax.currency = currencies[0];
      } else if (this.tax.currency && !currencies.includes(this.tax.currency)) {
        this.tax.currency = '';
      }
    },

    formatDecimal(val) {
      if (val === '' || val === null || val === undefined) return '';
      return String(val).replace('.', ',');
    },

    handleDecimalInput(field, min, max, event) {
      let val = event.target.value.replace(/\./g, ',');
      event.target.value = val;
      if (val === '') {
        this.tax[field] = '';
        return;
      }
      const num = parseFloat(val.replace(',', '.'));
      if (!isNaN(num)) {
        this.tax[field] = Math.min(max, Math.max(min, num));
      }
    }
  },
  computed: {
    // Micro fee threshold of the selected currency, e.g. "152.99 UAH". Empty until a currency is chosen.
    microThreshold() {
      const currency = this.tax.currency;
      if (!currency) {
        return '';
      }
      const country = this.countries.find(c => c.value === this.selectedCountry);
      const source = country?.fee_thresholds?.[currency] ? [country] : this.countries;
      const micro = source.map(c => c.fee_thresholds?.[currency]?.micro).find(v => v !== undefined);
      return micro === undefined ? '' : `${micro} ${currency}`;
    },
    // Currencies of the selected country, or of all countries when none is selected.
    // The rule's saved currency stays selectable even if the country config no longer lists it.
    availableCurrencies() {
      const country = this.countries.find(c => c.value === this.selectedCountry);
      const source = country ? [country] : this.countries;
      const currencies = new Set(source.flatMap(c => c.registrationCurrencies || []));
      if (this.tax.currency) {
        currencies.add(this.tax.currency);
      }
      return [...currencies].sort();
    },
  },
  watch: {
    'tax.type'(newVal) {
      if (Number(newVal) === 1) {
        this.getCountries();
      }
    }
  },
  mounted() {
    const id = this.$route.params.id;
    this.getCountries();
    window.scrollTo(0, 0);
    if (id) {
      this.fetchTax(id);
    }
  },
};
</script>

<style scoped>
.cutax-form-card {
  max-width: 720px;
  width: 100%;
}

.cutax-field-type {
  width: 120px;
}

.cutax-field-currency {
  width: 100px;
}

.cutax-field-country {
  width: 200px;
  min-width: 180px;
}

.cutax-field-date {
  width: 160px;
}

.cutax-field-vat {
  max-width: 120px;
}

.cutax-fee-row {
  align-items: flex-start;
}

.cutax-fee-field {
  width: 100px;
  min-width: 100px;
}

.cutax-fee-field-wide {
  flex: 1 1 280px;
  width: auto;
}

.cutax-form .form-control {
  max-width: 100%;
}

/* Align button row with form card */
.cutax-form > div:last-of-type .cutax-form-card {
  display: flex;
  justify-content: flex-end;
}
</style>

